import {startOfDay} from 'date-fns';
import {
  Button,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Box, Grid, Link, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';

import {isNotNil} from 'ramda';
import {isDate} from 'ramda-adjunct';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import {useCreateRentalOrderMutation, useReserveRentalOrderVehicleMutation} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {customerRoutes} from '@dms/routes';
import {CustomerInformationCard, CustomerMatchOrCreate, handleApiError} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useOverlappingRentalEvents} from '../hooks/useOverlappingRentalEvents';
import type {RentalCalendarPeriod} from '../types/RentalCalendarPeriod';
import {RentalCalendarVehicleSelect} from './RentalCalendarVehicleSelect';
import {RentalPeriodConflictAlert} from './RentalPeriodConflictAlert';

interface RentalCalendarCreateDialogProps extends RequiredTestIdProps {
  vehicleId: string;
  vehicleTitle?: string;
  slotStart: Date;
  slotEnd: Date;
  onCreated: (rentalOrderId: string) => void;
  initialCustomer?: CustomerResponseBodyV2 | null;
  onCustomerChange?: (customer: CustomerResponseBodyV2 | null) => void;
  onPeriodChange?: (period: RentalCalendarPeriod) => void;
  onVehicleChange?: (vehicleId: string) => void;
}

interface RentalPeriodFormValues {
  from: Date;
  to: Date;
}

export function RentalCalendarCreateDialog(props: RentalCalendarCreateDialogProps) {
  const [vehicleId, setVehicleId] = useState(props.vehicleId);
  const [customer, setCustomer] = useState<CustomerResponseBodyV2 | null>(
    props.initialCustomer ?? null
  );
  const [period, setPeriod] = useState<RentalCalendarPeriod>({
    from: props.slotStart,
    to: props.slotEnd,
  });

  // An empty list while the check is in flight means "unknown", not "clear", so submit waits for it
  const {events: overlappingEvents, isFetching: areOverlapsUnknown} = useOverlappingRentalEvents(
    vehicleId,
    period.from,
    period.to
  );

  const updateVehicle = (nextVehicleId: string) => {
    setVehicleId(nextVehicleId);
    props.onVehicleChange?.(nextVehicleId);
  };

  const updateCustomer = (nextCustomer: CustomerResponseBodyV2) => {
    setCustomer(nextCustomer);
    props.onCustomerChange?.(nextCustomer);
  };

  /** Re-opens the search so a customer picked by mistake can be replaced */
  const clearCustomer = () => {
    setCustomer(null);
    props.onCustomerChange?.(null);
  };

  const updatePeriod = (nextPeriod: RentalCalendarPeriod) => {
    setPeriod(nextPeriod);
    props.onPeriodChange?.(nextPeriod);
  };

  const [createRentalOrder, {isLoading: isCreating}] = useCreateRentalOrderMutation();
  const [reserveRentalVehicle, {isLoading: isReserving}] = useReserveRentalOrderVehicleMutation();

  const handleSubmit: FormSubmitHandler<RentalPeriodFormValues> = async (values) => {
    await createRentalOrder({
      createRentalOrderRequestBody: {
        rentalVehicleId: vehicleId,
        customerId: customer?.id ?? null,
        rentalPeriod: {from: values.from.toISOString(), to: values.to.toISOString()},
      },
    })
      .unwrap()
      // A vehicle plus a customer is what the manual flow reserves on, so match it here
      .then(async (data) => {
        // The order exists from here on. A failed reservation is reported by `handleApiError` and
        // must not stop the navigation below: the order would be left unreachable and submitting
        // again would create a second one instead of reserving the first
        if (isNotNil(customer)) {
          await reserveRentalVehicle({rentalOrderId: data.id}).unwrap().catch(handleApiError);
        }

        return data.id;
      })
      .then((rentalOrderId) => {
        closeCurrentDialog();
        props.onCreated(rentalOrderId);
      })
      .catch(handleApiError);
  };

  return (
    <Form<RentalPeriodFormValues>
      onSubmit={handleSubmit}
      onChange={(values) => updatePeriod({from: values.from, to: values.to})}
      experimentalZodSchema={schema}
      defaultValues={{from: props.slotStart, to: props.slotEnd}}
    >
      {(control, formApi) => {
        const fromDate = formApi.watch('from');

        return (
          <VStack spacing={4}>
            {/* react-select leaves Enter unhandled on a closed or no-match menu, and the form
                would submit instead of the search picking a vehicle */}
            <Box
              onKeyDownCapture={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            >
              <RentalCalendarVehicleSelect
                value={vehicleId}
                fallbackId={props.vehicleId}
                fallbackTitle={props.vehicleTitle}
                onChange={updateVehicle}
                data-testid={suffixTestId('createDialog', props)}
              />
            </Box>

            <Grid columns={2} align="flex-end">
              <FormField
                name="from"
                type="dateTime"
                control={control}
                isRequired
                label={i18n.t('page.rentalOrder.labels.rent')}
                data-testid={suffixTestId('createDialog-from', props)}
              />
              <FormField
                name="to"
                type="dateTime"
                control={control}
                minDate={isDate(fromDate) ? startOfDay(fromDate) : undefined}
                isRequired
                label={i18n.t('page.rentalOrder.labels.return')}
                data-testid={suffixTestId('createDialog-to', props)}
              />
            </Grid>

            <RentalPeriodConflictAlert
              events={overlappingEvents}
              data-testid={suffixTestId('createDialog', props)}
            />

            <Separator spacing={0} />

            {isNotNil(customer) ? (
              // The card's own detail link navigates in place, closing this dialog and losing
              // the half-filled rental order, so it is replaced by one opening a tab
              <CustomerInformationCard
                customerId={customer.id}
                onChange={clearCustomer}
                headerAction={
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    leftIcon="action/open_in_new"
                    href={composePath(customerRoutes.detail, {
                      params: {id: customer.id},
                      isSearchParamsPreserved: false,
                    })}
                    title={i18n.t('general.labels.detail')}
                    data-testid={suffixTestId('createDialog-customerDetail', props)}
                  />
                }
                data-testid={suffixTestId('createDialog-customerInformation', props)}
              />
            ) : (
              <Card variant="inlineWhite" title={i18n.t('page.rentalOrder.labels.customer')}>
                <CustomerMatchOrCreate
                  onCustomer={updateCustomer}
                  vehicleId={vehicleId}
                  data-testid={suffixTestId('createDialog-customer', props)}
                />
              </Card>
            )}

            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.cancel')}
                onClick={closeCurrentDialog}
                data-testid={suffixTestId('createDialog-cancel', props)}
              />
              <FormButton
                control={control}
                type="submit"
                variant="primary"
                // `isLoading` only sets `pointer-events: none`, which keyboard activation walks through
                isDisabled={areOverlapsUnknown}
                isLoading={isCreating || isReserving || areOverlapsUnknown}
                title={i18n.t('general.actions.create')}
                data-testid={suffixTestId('createDialog-submit', props)}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}

const schema = z
  .object({from: z.date(), to: z.date()})
  .refine((values) => values.from < values.to, {
    message: i18n.t('page.rentalOrder.errors.toBeforeFromError'),
    path: ['from'],
  });
